import { mintScheduledBillingReconciliationContext } from "../reconciliation/context.ts";
import { mintManualBillingReconciliationContext } from "../reconciliation/context.ts";
import { createPostgresBillingReconciliationLeaseStore } from "../reconciliation/lease.ts";
import { listActiveBillingImportConnections } from "../import/postgres.ts";
import type { BillingSqlExecutor } from "../subject/repository.ts";
import { createPostgresBillingAuditWriter } from "../observability/audit.ts";
import { normalizeAthenaBillingObservability } from "../observability/config.ts";
import {
	createDisabledBillingTraceRecorder,
	createPostgresBillingTraceRecorder,
} from "../observability/traces.ts";
import type { NormalizedAthenaBillingObservability } from "../observability/types.ts";
import { createConnectionProviderExecutionContext } from "../runtime/local/providers/connection-binding.ts";
import type { BillingProviderConfigMap } from "../providers/types.ts";
import { resolveMollieWebhookManagementCapability } from "../runtime/local/providers/mollie/webhook-capability.ts";
import type { BillingProviderRegistry } from "../runtime/local/providers/registry.ts";
import {
	billingWebhookUrlIsLoopback,
	resolveBillingIngressEndpoints,
} from "./urls.ts";
import type { NormalizedAthenaBillingWebhooksConfig } from "./types.ts";
import { reconcileBillingWebhookRegistrations } from "./reconciliation/coordinator.ts";
import { createPostgresBillingWebhookRegistrationStore } from "./reconciliation/repository.ts";

const warnedLoopbackWebhookUrls = new Set<string>();

export async function runAutomaticBillingWebhookReconcileIfEnabled(input: {
	applicationId: string;
	appUrl?: string | null;
	configuredProviders?: BillingProviderConfigMap;
	observability?: NormalizedAthenaBillingObservability;
	registry?: BillingProviderRegistry;
	sql: BillingSqlExecutor;
	testMode?: boolean;
	trigger: "bootstrap" | "scheduled" | "manual";
	webhooks: NormalizedAthenaBillingWebhooksConfig;
}): Promise<void> {
	if (!input.webhooks.enabled) {
		return;
	}
	const endpoints = resolveBillingIngressEndpoints({
		appUrl: input.appUrl,
		publicBaseUrl: input.webhooks.publicBaseUrl,
	});
	if (!endpoints) {
		return;
	}
	const skipProviderWebhookApi = billingWebhookUrlIsLoopback(
		endpoints.eventsUrl,
	);
	if (
		skipProviderWebhookApi &&
		!warnedLoopbackWebhookUrls.has(endpoints.eventsUrl)
	) {
		warnedLoopbackWebhookUrls.add(endpoints.eventsUrl);
		console.warn(
			"[athena-billing] skipping Mollie next-gen webhook registration; the public URL is loopback and Mollie cannot reach it",
			{ url: endpoints.eventsUrl },
		);
	}
	const connections = await listActiveBillingImportConnections(
		input.sql,
		"mollie",
	);
	const observability =
		input.observability ?? normalizeAthenaBillingObservability();
	const traces = observability.traces.enabled
		? createPostgresBillingTraceRecorder(
				input.sql,
				observability.traces.sampleRate,
			)
		: createDisabledBillingTraceRecorder();
	const audit = observability.auditLog
		? createPostgresBillingAuditWriter(input.sql)
		: undefined;
	const mollie = input.configuredProviders?.mollie;
	if (mollie == null && input.configuredProviders?.mollieAccounts == null) {
		return;
	}
	const runtime = input.registry?.get("mollie");
	for (const connection of connections) {
		const context =
			input.trigger === "scheduled"
				? mintScheduledBillingReconciliationContext({
						connectionId: connection.id,
						provider: connection.provider,
						schedulerExecutionId: crypto.randomUUID(),
					})
				: mintManualBillingReconciliationContext({
						connectionId: connection.id,
						provider: connection.provider,
						trigger: input.trigger === "bootstrap" ? "bootstrap" : "manual",
					});
		const execution = createConnectionProviderExecutionContext({
			configuredProviders: input.configuredProviders,
			connection,
			ingress: { classicWebhookUrl: endpoints.classicUrl },
			operationScope: "organization",
		});
		const capability = resolveMollieWebhookManagementCapability(
			execution.binding,
		);
		await reconcileBillingWebhookRegistrations({
			audit,
			context,
			execution,
			lease: createPostgresBillingReconciliationLeaseStore(input.sql),
			port: skipProviderWebhookApi ? undefined : runtime?.webhooks,
			reconcile: {
				applicationId: input.applicationId,
				capability,
				connectionId: connection.id,
				endpoints,
				environment: connection.environment,
				management: input.webhooks.management,
				provider: connection.provider,
				webhooks: input.webhooks,
			},
			registrations: createPostgresBillingWebhookRegistrationStore(input.sql),
			traces,
		});
	}
}
